/**
 * <StudyInsights> — /study/insights
 *
 * Student-facing Recall/Connect feedback page. Answers three questions:
 *   1. How well am I retaining? (Recall Mastery + streak)
 *   2. Am I connecting concepts across modules? (Connect Strength on
 *      Bridge cards)
 *   3. How am I performing in peer duels? (Elo + W-L)
 *
 * Also surfaces the top-3 cards to review next, and the 3 concepts the
 * student is nailing (positive reinforcement). Tone stays encouraging
 * per the design spec — no leaderboards, no percentile shaming.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { STUDY_THEME, paperBackground, CARD_TYPE_META } from '../components/study/studyTheme';
import { getMyInsights } from '../lib/studyApi';

const cardStyle = {
  background: 'white', borderRadius: 14, padding: 24,
  boxShadow: STUDY_THEME.shadow, border: `1px solid ${STUDY_THEME.line}`,
};

const kpi = (val, label, sub, colorHint) => (
  <div style={{ ...cardStyle, textAlign: 'center' }} data-testid={`kpi-${label}`}>
    <div style={{
      fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase',
      fontWeight: 700, color: STUDY_THEME.goldDeep, marginBottom: 8,
    }}>{label}</div>
    <div style={{
      fontFamily: '"Playfair Display", serif', fontSize: 44,
      fontWeight: 700, color: colorHint || STUDY_THEME.ink, lineHeight: 1,
    }}>{val}</div>
    {sub && (
      <div style={{ fontSize: 12, color: STUDY_THEME.muted, marginTop: 6 }}>
        {sub}
      </div>
    )}
  </div>
);

const encouragingLine = (mastery) => {
  if (mastery >= 85) return 'You are on fire — keep the streak alive.';
  if (mastery >= 65) return 'Solid retention — one more pass tightens it.';
  if (mastery >= 40) return 'Coming together — the weak spots below want another look.';
  if (mastery > 0)   return 'You are early in the loop — a few short reviews compound quickly.';
  return 'Start a Study session below and your mastery will begin tracking.';
};

// A very small SVG sparkline — 7 daily mastery buckets.
function Sparkline({ points }) {
  if (!points || points.length < 2) return null;
  const w = 240, h = 44, pad = 4;
  const max = Math.max(...points.map((p) => p.mastery), 100);
  const min = 0;
  const stepX = (w - pad * 2) / (points.length - 1);
  const y = (v) => h - pad - ((v - min) / (max - min || 1)) * (h - pad * 2);
  const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${pad + i * stepX} ${y(p.mastery)}`).join(' ');
  return (
    <svg width={w} height={h} style={{ display: 'block', marginTop: 8 }}
         data-testid="mastery-sparkline">
      <path d={d} fill="none" stroke={STUDY_THEME.goldDeep} strokeWidth={2.2} strokeLinejoin="round" />
      {points.map((p, i) => (
        <circle key={i} cx={pad + i * stepX} cy={y(p.mastery)} r={2.5}
                fill={STUDY_THEME.goldDeep} />
      ))}
    </svg>
  );
}

export default function StudyInsights() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    getMyInsights().then(setData).catch((e) => setErr(e.message || 'Load failed'));
  }, []);

  return (
    <div style={paperBackground()} data-testid="study-insights-page">
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '40px 24px' }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: 32,
        }}>
          <div>
            <div style={{
              fontSize: 11, letterSpacing: 1.6, textTransform: 'uppercase',
              fontWeight: 700, color: STUDY_THEME.goldDeep,
            }}>Institute of Analytics · Active Recall</div>
            <h1 style={{
              fontFamily: '"Playfair Display", serif', fontSize: 40,
              margin: '2px 0 6px', color: STUDY_THEME.ink,
            }}>🧠 Your Recall & Connect</h1>
            {data && (
              <p style={{ color: STUDY_THEME.inkSoft, margin: 0, fontSize: 15 }}>
                {encouragingLine(data.recall_mastery)}
              </p>
            )}
          </div>
          <button
            onClick={() => navigate('/study')}
            data-testid="insights-back-hub"
            style={{
              padding: '8px 14px', fontSize: 13, fontWeight: 600,
              background: 'white', color: STUDY_THEME.inkSoft,
              border: `1px solid ${STUDY_THEME.line}`, borderRadius: 6,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >← Deck Hub</button>
        </div>

        {err && (
          <div data-testid="insights-error" style={{
            padding: 16, borderRadius: 10, background: '#FEE2E2',
            color: '#991B1B', marginBottom: 20,
          }}>{err}</div>
        )}

        {!data ? (
          <div style={{ padding: 40, textAlign: 'center', color: STUDY_THEME.muted }}>
            Crunching your signal…
          </div>
        ) : (
          <>
            {/* G3.3 · Weekly reflection digest */}
            <WeeklyDigestTile />

            {/* Headline KPIs */}
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 16, marginBottom: 24,
            }}>
              {kpi(
                `${data.recall_mastery}%`,
                'recall-mastery',
                `across ${data.cards_reviewed} card${data.cards_reviewed === 1 ? '' : 's'}`,
              )}
              {kpi(
                `${data.connect_strength}%`,
                'connect-strength',
                'bridge cards only',
              )}
              {kpi(
                data.streak_days,
                'streak',
                data.streak_days > 0 ? '🔥 days in a row' : 'start today',
              )}
              {kpi(
                data.peer_elo,
                'peer-rating',
                data.peer_delta_7d > 0 ? `↑ ${data.peer_delta_7d} this week`
                : data.peer_delta_7d < 0 ? `↓ ${Math.abs(data.peer_delta_7d)} this week`
                : 'no change this week',
                data.peer_delta_7d > 0 ? '#16A34A'
                : data.peer_delta_7d < 0 ? '#DC2626' : STUDY_THEME.ink,
              )}
            </div>

            {/* Trend + duel record */}
            <div style={{
              display: 'grid', gridTemplateColumns: '2fr 1fr',
              gap: 16, marginBottom: 24,
            }}>
              <div style={cardStyle} data-testid="trend-card">
                <div style={{
                  fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase',
                  fontWeight: 700, color: STUDY_THEME.goldDeep, marginBottom: 6,
                }}>7-day mastery trend</div>
                <div style={{
                  fontFamily: '"Playfair Display", serif', fontSize: 22,
                  color: STUDY_THEME.ink, margin: '2px 0 2px',
                }}>Where retention is heading</div>
                <div style={{ fontSize: 13, color: STUDY_THEME.inkSoft, marginBottom: 8 }}>
                  Each dot is your mastery blend on that day. Consistent
                  reviews keep the curve up and to the right.
                </div>
                <Sparkline points={data.trend_7d} />
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  fontSize: 10, color: STUDY_THEME.muted, marginTop: 4,
                }}>
                  {data.trend_7d.map((p) => (
                    <span key={p.date}>{p.date.slice(5)}</span>
                  ))}
                </div>
              </div>

              <div style={cardStyle} data-testid="duel-record-card">
                <div style={{
                  fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase',
                  fontWeight: 700, color: STUDY_THEME.goldDeep, marginBottom: 6,
                }}>Buddy Duels</div>
                <div style={{
                  fontFamily: '"Playfair Display", serif', fontSize: 44,
                  fontWeight: 700, color: STUDY_THEME.ink, lineHeight: 1,
                }}>
                  {data.duel_record.wins}
                  <span style={{ fontSize: 22, color: STUDY_THEME.muted }}> / </span>
                  {data.duel_record.losses}
                  {data.duel_record.ties > 0 && (
                    <span style={{ fontSize: 22, color: STUDY_THEME.muted }}> / {data.duel_record.ties}</span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: STUDY_THEME.muted, marginTop: 4 }}>
                  wins / losses{data.duel_record.ties > 0 && ' / ties'}
                </div>
                <button
                  onClick={() => navigate('/study/duels')}
                  data-testid="insights-view-duels"
                  style={{
                    marginTop: 14, padding: '8px 14px', fontSize: 12,
                    fontWeight: 700, background: STUDY_THEME.gold,
                    color: STUDY_THEME.ink,
                    border: `1px solid ${STUDY_THEME.goldDark}`, borderRadius: 6,
                    cursor: 'pointer', fontFamily: 'inherit', width: '100%',
                  }}
                >🥊 See Duel History</button>
              </div>
            </div>

            {/* Weakest + Best cards */}
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr',
              gap: 16, marginBottom: 24,
            }}>
              <div style={cardStyle} data-testid="weakest-card">
                <div style={{
                  fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase',
                  fontWeight: 700, color: '#DC2626', marginBottom: 6,
                }}>🎯 Try these next</div>
                <div style={{
                  fontFamily: '"Playfair Display", serif', fontSize: 20,
                  color: STUDY_THEME.ink, marginBottom: 14,
                }}>3 concepts want another pass</div>
                {data.weakest.length === 0 ? (
                  <div style={{ color: STUDY_THEME.muted, fontSize: 13 }}>
                    Review a few more cards and this list will populate.
                  </div>
                ) : data.weakest.map((c) => {
                  const meta = CARD_TYPE_META[c.type] || CARD_TYPE_META.standard;
                  return (
                    <button
                      key={c.card_id}
                      onClick={() => navigate(`/study/session?deck=${c.deck_id || ''}`)}
                      data-testid={`weakest-item-${c.card_id}`}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left',
                        padding: '10px 12px', border: `1px solid ${STUDY_THEME.line}`,
                        borderRadius: 8, background: 'white',
                        marginBottom: 8, cursor: 'pointer', fontFamily: 'inherit',
                      }}
                    >
                      <div style={{
                        display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', marginBottom: 4,
                      }}>
                        <span style={{
                          fontSize: 10, fontWeight: 700, letterSpacing: 1,
                          color: meta.accent, textTransform: 'uppercase',
                        }}>{meta.emoji} {meta.label}</span>
                        <span style={{
                          fontSize: 12, fontWeight: 700,
                          color: c.mastery >= 50 ? STUDY_THEME.goldDark : '#DC2626',
                        }}>{c.mastery.toFixed(0)}%</span>
                      </div>
                      <div style={{ fontSize: 13, color: STUDY_THEME.inkSoft, lineHeight: 1.4 }}>
                        {c.front}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div style={cardStyle} data-testid="best-card">
                <div style={{
                  fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase',
                  fontWeight: 700, color: '#16A34A', marginBottom: 6,
                }}>💪 You&apos;ve nailed these</div>
                <div style={{
                  fontFamily: '"Playfair Display", serif', fontSize: 20,
                  color: STUDY_THEME.ink, marginBottom: 14,
                }}>3 concepts you own</div>
                {data.best.length === 0 ? (
                  <div style={{ color: STUDY_THEME.muted, fontSize: 13 }}>
                    Rate a few cards Good/Easy across 3+ reviews to earn this list.
                  </div>
                ) : data.best.map((c) => {
                  const meta = CARD_TYPE_META[c.type] || CARD_TYPE_META.standard;
                  return (
                    <div key={c.card_id}
                         data-testid={`best-item-${c.card_id}`}
                         style={{
                           padding: '10px 12px', border: `1px solid ${STUDY_THEME.line}`,
                           borderRadius: 8, background: '#F0FDF4',
                           marginBottom: 8,
                         }}>
                      <div style={{
                        display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', marginBottom: 4,
                      }}>
                        <span style={{
                          fontSize: 10, fontWeight: 700, letterSpacing: 1,
                          color: meta.accent, textTransform: 'uppercase',
                        }}>{meta.emoji} {meta.label}</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#16A34A' }}>
                          {c.mastery.toFixed(0)}%
                        </span>
                      </div>
                      <div style={{ fontSize: 13, color: STUDY_THEME.inkSoft, lineHeight: 1.4 }}>
                        {c.front}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <button
                onClick={() => navigate('/study')}
                data-testid="insights-start-session"
                style={{
                  padding: '14px 32px', fontSize: 15, fontWeight: 700,
                  background: STUDY_THEME.gold, color: STUDY_THEME.ink,
                  border: `1px solid ${STUDY_THEME.goldDark}`, borderRadius: 10,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                Start a review session →
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * G3.3 — Weekly reflection digest tile.
 * Reads `/api/insights/student/weekly-digest` and renders the 3-line
 * Haiku-generated digest. Cached in localStorage for the day so we don't
 * hammer the LLM on every visit.
 */
function WeeklyDigestTile() {
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const cacheKey = 'insights.weekly_digest_v1';

  const load = async (force = false) => {
    setLoading(true); setErr('');
    try {
      if (!force) {
        const cached = JSON.parse(localStorage.getItem(cacheKey) || 'null');
        const today = new Date().toISOString().slice(0, 10);
        if (cached && cached.day === today) {
          setDigest(cached.data); setLoading(false); return;
        }
      }
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token') || '';
      const r = await fetch(`${API_URL}/api/insights/student/weekly-digest`, {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setDigest(d);
      const today = new Date().toISOString().slice(0, 10);
      localStorage.setItem(cacheKey, JSON.stringify({ day: today, data: d }));
    } catch (e) {
      setErr(e.message || 'Digest unavailable');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(false); }, []);

  const lines = (digest?.digest || '').split(/\n+/).map((l) => l.trim()).filter(Boolean);

  return (
    <div data-testid="weekly-digest-tile" style={{
      background: 'linear-gradient(135deg, #FFF9E6 0%, #FFFDF9 100%)',
      border: `1px solid ${STUDY_THEME.goldDark}`,
      borderRadius: 14, padding: 22, marginBottom: 24,
      boxShadow: STUDY_THEME.shadow,
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', gap: 12, flexWrap: 'wrap',
      }}>
        <div style={{ flex: '1 1 auto' }}>
          <div style={{
            fontSize: 11, letterSpacing: 1.6, textTransform: 'uppercase',
            fontWeight: 700, color: STUDY_THEME.goldDeep, marginBottom: 4,
          }}>🧠 Your week, from a coach&apos;s eye</div>
          <div style={{ fontSize: 12, color: STUDY_THEME.muted }}>
            Auto-written from your last 7 days of chats, cards, duels &amp; reflections.
          </div>
        </div>
        <button
          onClick={() => load(true)}
          disabled={loading}
          data-testid="weekly-digest-refresh"
          style={{
            padding: '6px 12px', fontSize: 12, fontWeight: 600,
            background: 'white', color: STUDY_THEME.inkSoft,
            border: `1px solid ${STUDY_THEME.line}`, borderRadius: 6,
            cursor: loading ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit', opacity: loading ? 0.5 : 1,
          }}
        >{loading ? 'Regenerating…' : '↻ Refresh'}</button>
      </div>

      {err && (
        <div style={{ color: '#991B1B', fontSize: 13, marginTop: 12 }}>{err}</div>
      )}

      {digest && lines.length > 0 && (
        <ol data-testid="weekly-digest-lines" style={{
          margin: '14px 0 0', paddingLeft: 22, listStylePosition: 'outside',
          color: STUDY_THEME.ink, fontSize: 15, lineHeight: 1.55,
        }}>
          {lines.slice(0, 3).map((l, i) => (
            <li key={i} style={{ marginBottom: 6 }}>{l}</li>
          ))}
        </ol>
      )}

      {digest?.signals && (
        <div style={{ marginTop: 12, fontSize: 11, color: STUDY_THEME.muted, letterSpacing: 0.3 }}>
          this week · {digest.signals.reviews_count} cards · {digest.signals.chat_count} tutor chats · {digest.signals.duels_won}/{digest.signals.duels_total} duels won · {digest.signals.reflections_count} reflections
        </div>
      )}
    </div>
  );
}
